import { useEffect, useState, useCallback } from "react";
import { fetchSubscriptionStatus } from "../services/api/subscription";

type UseSubscriptionForecastOptions = {
  getForecast: () => Promise<string>;
  cacheValue: string | null;
  setCache: (val: string) => void;
};

export const useSubscriptionForecast = ({
  getForecast,
  cacheValue,
  setCache,
}: UseSubscriptionForecastOptions) => {
  const [forecast, setForecast] = useState<string | null>(null);
  const [subscriptionMessage, setSubscriptionMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadForecast = useCallback(async () => {
    setLoading(true);
    setError(null);
    setForecast(null);
    setSubscriptionMessage(null);

    try {
      // 1. Проверяем подписку
      const subStatus = await fetchSubscriptionStatus();
      if (!subStatus.isActive) {
        setSubscriptionMessage(subStatus.message);
        setLoading(false);
        return;
      }

      // 2. Кэш
      if (cacheValue) {
        setForecast(cacheValue);
        setLoading(false);
        return;
      }

      // 3. Получаем прогноз
      const result = await getForecast();
      setForecast(result);
      setCache(result);
    } catch (e) {
      const err = e as {
        response?: { data?: { message?: string; error?: string } };
        message?: string;
      };

      const message =
        err?.response?.data?.error || err?.response?.data?.message || err?.message || "";

      if (
        typeof message === "string" &&
        (message.includes("available in a few hours") ||
          message.includes("requires a subscription"))
      ) {
        setError("Прогноз будет доступен через несколько часов. Пожалуйста, попробуйте позже.");
      } else {
        setError("Не удалось загрузить прогноз. Попробуйте снова.");
      }
    } finally {
      setLoading(false);
    }
  }, [getForecast, cacheValue, setCache]);

  useEffect(() => {
    loadForecast();
    // eslint-disable-next-line
  }, []);

  return {
    forecast,
    subscriptionMessage,
    loading,
    error,
    reload: loadForecast,
  };
};
